import { IPoint } from 'office-ui-fabric-react/lib/utilities/positioning';
import { addDatapointConfig } from 'owa-analytics-actions';
import {
    ExpandedConversationViewState,
    MailRowDataPropertyGetter,
    SelectionDirection,
    TableQueryType,
    TableView,
} from 'owa-mail-list-store';
import { MailListItemSelectionSource } from 'owa-mail-store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { action } from 'satcheljs';

export { ReactListViewType, TableQueryType, ExpandedConversationViewState };

/**
 * Single select mail row action
 * @param tableView where the operation is being performed
 * @param rowKey the rowKey of the item to select
 * @param isUserNavigation whether user's click or action triggered this action
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export const singleSelectRow = action(
    'SINGLE_SELECT_ROW',
    (
        tableView: TableView,
        rowKey: string,
        isUserNavigation: boolean,
        mailListItemSelectionSource: MailListItemSelectionSource
    ) => {
        const validCouponIndexes = MailRowDataPropertyGetter.getValidCouponIndexes(
            rowKey,
            tableView
        );
        return addDatapointConfig(
            {
                name: 'SelectMailItem',
                options: { isQos: true },
                customData: [
                    mailListItemSelectionSource,
                    tableView.tableQuery.listViewType,
                    tableView.tableQuery.type,
                    tableView.tableQuery.type === TableQueryType.Search &&
                        (tableView.tableQuery as any).searchScope.kind,
                    validCouponIndexes ? validCouponIndexes.length : 0,
                ],
            },
            {
                tableView,
                rowKey,
                isUserNavigation,
                mailListItemSelectionSource,
            }
        );
    }
);

/**
 * Toggle selection state on the given row
 * @param tableView where the selection took place
 * @param rowKey the rowKey of the item to toggle selection on
 * @param isUserNavigation whether user's click or action triggered this action
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export const toggleSelectRow = action(
    'TOGGLE_SELECT_ROW',
    (
        tableView: TableView,
        rowKey: string,
        isUserNavigation: boolean,
        mailListItemSelectionSource: MailListItemSelectionSource
    ) => ({
        tableView,
        rowKey,
        isUserNavigation,
        mailListItemSelectionSource,
    })
);

/**
 * After promise has returned following a selection change action
 * @param rowKey the rowKey of the item that selection changed on
 * @param tableView where the selection took place
 * @param mailListItemSelectionSource The source of selection on mail item
 * @param listViewType - list view type (e.g. conversation, item)
 */
export const onAfterSelectionChanged = action(
    'ON_AFTER_SELECTION_CHANGED',
    (
        rowKey: string,
        tableView: TableView,
        mailListItemSelectionSource: MailListItemSelectionSource,
        listViewType: ReactListViewType
    ) => ({
        rowKey,
        tableView,
        mailListItemSelectionSource,
        listViewType,
    })
);

/**
 * Set selection state on the given row
 * @param rowKey the rowKey of the item to toggle selection on
 * @param tableViewId of the table where the selection took place
 * @param shouldSelect - whether the row should be selected or deselected
 */
export const setSelectionOnRow = action(
    'SET_SELECTION_ON_ROW',
    (rowKey: string, tableViewId: string, shouldSelect: boolean) => {
        return {
            rowKey,
            tableViewId,
            shouldSelect,
        };
    }
);

/**
 * Reset the list view expansion state (expanded conversation)
 */
export const resetListViewExpansionViewState = action('RESET_LIST_VIEW_EXPANSION_VIEW_STATE');

/**
 * Set the list view expansion loading state
 */
export const setListViewExpansionLoadingState = action(
    'SET_LIST_VIEW_EXPANSION_LOADING_STATE',
    (shouldBeExpanded: boolean) => ({
        shouldBeExpanded,
    })
);

/**
 * Called when an item part is clicked
 * @param nodeId the nodeId of the clicked item part
 * @param itemId the itemId of the clicked item part
 * @param tableView - where the selection took place
 */
export const itemPartClicked = action(
    'ITEM_PART_CLICKED',
    (nodeId: string, itemId: string, allNodeIds: string[], tableView: TableView) => ({
        nodeId,
        itemId,
        allNodeIds,
        tableView,
    })
);

/**
 * Called when an item part is clicked
 * @param rowKey the rowKey for the parent conversation of the item part
 * @param tableView - where the delete took place
 */
export const selectNextItemPart = action(
    'SELECT_NEXT_ITEM_PART',
    (rowKey: string, tableView: TableView) => ({
        rowKey,
        tableView,
    })
);

/**
 * Called to open the context menu for an expanded item part
 * @param contextMenuAnchor anchor from which the context menu will open
 * @param nodeId the Id of the item part that was selected
 * @param allNodeIds all the node Ids for the conversation
 * @param tableViewId the id of the table where the conversation belongs
 */
export const onItemPartContextMenu = action(
    'ON_ITEM_PART_CONTEXT_MENU',
    (contextMenuAnchor: IPoint, nodeId: string, allNodeIds: string[], tableViewId: string) => ({
        contextMenuAnchor,
        nodeId,
        allNodeIds,
        tableViewId,
    })
);

/**
 * Select a single expanded item part
 * @param rowKey the instance key of the conversation
 * @param nodeIdToSelect the nodeId of the item part to select
 * @param allNodeIds of the conversation
 */
export const singleSelectItemPart = action(
    'SINGLE_SELECT_ITEM_PART',
    (rowKey: string, nodeIdToSelect: string, allNodeIds: string[], tableViewId: string) => ({
        rowKey,
        nodeIdToSelect,
        allNodeIds,
        tableViewId,
    })
);

/**
 * Toggle selection on an item part
 * @param nodeId the nodeId of the item part to toggle selection
 * @param allNodeIds of the conversation
 */
export const toggleSelectItemPart = action(
    'TOGGLE_SELECT_ITEM_PART',
    (nodeId: string, allNodeIds: string[]) => ({
        nodeId,
        allNodeIds,
    })
);

/**
 * Toggle between select all and clear selection on the table
 * @param tableView where the operation is being performed
 */
export const toggleSelectAllRows = action('TOGGLE_SELECT_ALL_ROWS', (tableView: TableView) => ({
    tableView,
}));

/**
 * Perform range selection from current selection to target row key
 * @param tableView where the selection took place
 * @param targetRowKey the rowKey of the item to extend range selection to
 * @param mailListItemSelectionSource The source of selection on mail item
 * @param isCtrlKeyDown - whether the Control key is down for this operation
 */
export const rangeSelectRow = action(
    'RANGE_SELECT_ROW',
    (
        tableView: TableView,
        targetRowKey: string,
        mailListItemSelectionSource: MailListItemSelectionSource,
        isCtrlKeyDown?: boolean
    ) => ({
        tableView,
        targetRowKey,
        mailListItemSelectionSource,
        isCtrlKeyDown,
    })
);

/**
 * Resets selection in the given table
 * @param tableView where the operation is being performed
 * @param mailListItemSelectionSource selection mode for the maillist item
 */
export const resetSelection = action(
    'RESET_SELECTION',
    (tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource) => ({
        tableView,
        mailListItemSelectionSource,
    })
);

/**
 * Resets selection in the given table
 * @param tableView where the operation is being performed
 */
export const resetSelectionOnTable = action('RESET_SELECTION', (tableView: TableView) => ({
    tableView,
}));

/**
 * Select the next row in the direction given
 * @param tableView where the operation is being performed
 * @param selectionDirection the direction in which to select next row
 * @param mailListItemSelectionSource selection mode for the maillist item
 */
export const selectRowInDirection = action(
    'SELECT_ROW_IN_DIRECTION',
    (
        tableView: TableView,
        selectionDirection: SelectionDirection,
        mailListItemSelectionSource: MailListItemSelectionSource
    ) =>
        addDatapointConfig(
            {
                name:
                    selectionDirection === SelectionDirection.Next
                        ? 'SelectNextMailRow'
                        : 'SelectPreviousMailRow',
                customData: [
                    mailListItemSelectionSource,
                    tableView.tableQuery.listViewType,
                    tableView.tableQuery.type,
                    tableView.tableQuery.type === TableQueryType.Search &&
                        (tableView.tableQuery as any).searchScope.kind,
                ],
            },
            {
                tableView,
                selectionDirection,
                mailListItemSelectionSource,
            }
        )
);

/**
 * Select the row in the direction given in multiselect mode
 * @param tableView where the operation is being performed
 * @param mailListItemSelectionSource selection mode for the maillist item
 * @param selectionDirection direction in which the keyboard multiselection is happening (shift-up == Next, shift-down == Previous)
 * @param isCtrlDown whether user is also holding down ctrl key
 */
export const keyboardMultiSelectRow = action(
    'KEYBOARD_MULTI_SELECT_ROW',
    (
        tableView: TableView,
        mailListItemSelectionSource: MailListItemSelectionSource,
        selectionDirection: SelectionDirection,
        isCtrlDown: boolean
    ) => ({
        tableView,
        mailListItemSelectionSource,
        selectionDirection,
        isCtrlDown,
    })
);

/**
 * Collapse a given conversation when a user gives a keydown command
 * @param rowKey the row to collapse
 */
export const keyboardCollapseConversation = action(
    'KEYBOARD_COLLAPSE_CONVERSATION',
    (rowKey: string) => ({
        rowKey,
    })
);

export const resetBusStopStateMap = action('RESET_BUS_STOP_STATE_MAP');

/**
 * Selects a new row on row removal
 * @param tableView where the operation is being performed
 * @param lastSelectedRowIndex index of the row that was last selected
 * @param lastSelectedRowWasPinned whether last selected row was pinned
 */
export const selectNewItemUponRowRemoval = action(
    'SELECT_NEW_ITEM_UPON_ROW_REMOVAL',
    (tableView: TableView, lastSelectedRowIndex: number, lastSelectedRowWasPinned: boolean) => ({
        tableView,
        lastSelectedRowIndex,
        lastSelectedRowWasPinned,
    })
);

/**
 * Initialize selection on load
 * @param tableView where the operation is being performed
 */
export const initTableSelectionOnLoad = action(
    'INIT_TABLE_SELECTION_ON_LOAD',
    (tableView: TableView) => ({
        tableView,
    })
);
